/** @jsx jsx */
import React, { type PropsWithChildren, forwardRef, memo, useMemo, Fragment } from 'react';
import { css, jsx, styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import Avatar from '@atlaskit/avatar';
import AvatarGroup from '@atlaskit/avatar-group';
import { token } from '@atlaskit/tokens';
import { Text } from '@atlaskit/primitives/compiled';
import { AvatarRelay } from '@atlassian/avatar-relay';
import { useIntl } from '@atlassian/jira-intl';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';

import { useBespokeEmptyFieldTextExperiment } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-experiment/index.tsx';
import { VIEW_PROFILE_ACTION } from '@atlassian/jira-profilecard-next/src/common/constants.tsx';
import type { Actions } from '@atlassian/jira-profilecard-next/src/common/types.tsx';
import ProfileCard from '@atlassian/jira-profilecard-next/src/ui/profilecard/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import type { singleUserPicker_issueFieldSingleUserPickerReadviewFull_SingleUserPickerWithProfileCardReadView_fragmentRef$key as SingleUserPickerWithProfileCardFragment } from './__generated__/singleUserPicker_issueFieldSingleUserPickerReadviewFull_SingleUserPickerWithProfileCardReadView_fragmentRef.graphql';
import type { singleUserPicker_issueFieldSingleUserPickerReadviewFull_SingleUserPickerReadView_fragmentRef$key as SingleUserPickerFragment } from './__generated__/singleUserPicker_issueFieldSingleUserPickerReadviewFull_SingleUserPickerReadView_fragmentRef.graphql';
import messages from './messages.tsx';
import type {
	SingleUserPickerReadViewProps,
	SingleUserPickerWithProfileCardReadViewProps,
} from './types.tsx';

// In this case we are lifting the UFO segment to the fields using this shared component
const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	Fragment,
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="issue-field-single-user-picker-read-view" mode="list">
			{children}
		</UFOSegment>
	),
);

/**
 * The SingleUserPickerReadView will show a read only view of SingleUserPicker field.
 * @param props [SingleUserPickerReadViewProps](./types.tsx)
 */
export const SingleUserPickerReadView = memo(
	({
		fragmentRef,
		emptyUserLabel = null,
		shouldRenderHoverPopover,
		fieldId,
		humanSupervisor,
	}: SingleUserPickerReadViewProps): JSX.Element => {
		const data = useFragment<SingleUserPickerFragment>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment singleUserPicker_issueFieldSingleUserPickerReadviewFull_SingleUserPickerReadView_fragmentRef on JiraSingleSelectUserPickerField
				@argumentDefinitions(
					isJiraAiAgentAvatarSingleUserPickerEnabled: {
						type: "Boolean!"
						provider: "@atlassian/jira-relay-provider/src/jira-ai-agent-avatar-single-user-picker.relayprovider"
					}
				) {
					id
					user {
						name
						picture
						accountStatus
						...avatarRelay_UsePictureAndAppTypeFragment
							@include(if: $isJiraAiAgentAvatarSingleUserPickerEnabled)
					}
				}
			`,
			fragmentRef,
		);

		const { formatMessage } = useIntl();
		const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();

		const {
			name = emptyUserLabel ?? formatMessage(messages.anonymousOption),
			picture,
			accountStatus,
		} = data?.user || {};

		const hoverPopoverRefFactory = shouldRenderHoverPopover
			? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
				useHoverPopoverContentRef(data.id)
			: null;

		if (!data?.user && fieldId !== 'assignee' && fieldId !== 'reporter' && isExperimentEnabled) {
			return <EmptyFieldText emptyText={formatMessage(messages.noValueTextExperiment)} />;
		}

		let hoverPopoverRef: HoverPopoverContentRef = null;
		if (hoverPopoverRefFactory) {
			hoverPopoverRef = hoverPopoverRefFactory({
				content: name,
			});
		}

		if (shouldRenderHoverPopover && data?.user === null) {
			return (
				<Container>
					<Avatar
						as={AvatarAlignmentFix}
						src={picture}
						name={name}
						size="small"
						borderColor="transparent"
					/>
					<Text maxLines={1} ref={hoverPopoverRef}>
						{name}
					</Text>
				</Container>
			);
		}

		// TODO: to be either cleaned up in CRCS-3113 or replaced with a unified solution
		if (humanSupervisor && fg('jira_ai_show_human_supervisor_avatar')) {
			return (
				<Container>
					<AvatarGroup
						appearance="stack"
						data={[
							{ name, src: picture, appearance: 'hexagon' },
							{ name, src: humanSupervisor?.avatarUrls.large },
						]}
					/>

					<TextContainer>
						{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
						<span aria-label="human supervisor">
							{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
							{name} with {humanSupervisor?.displayName}
						</span>
						{data?.user && accountStatus !== 'active' && (
							// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
							<UserStatus>{`(${formatMessage(messages.deactivated)})`}</UserStatus>
						)}
					</TextContainer>
				</Container>
			);
		}

		return (
			// We use our own container element rather than an AvatarItem as we've observed hydration bugs with SSR+Emotion
			// when this component is wrapped in an async profile card, and this approach is the path of least friction.
			// Further context here https://atlassian.slack.com/archives/C060GTVV988/p1712624943348969.
			<Container>
				{fg('jira_ai_agent_avatar_single_user_picker') ? (
					<AvatarRelay
						as={AvatarAlignmentFix}
						fragmentRef={data.user ?? null}
						name={name}
						size="small"
						borderColor="transparent"
					/>
				) : (
					<Avatar
						as={AvatarAlignmentFix}
						src={picture}
						name={name}
						size="small"
						borderColor="transparent"
					/>
				)}
				<TextContainer>
					<span>{name}</span>
					{data?.user && accountStatus !== 'active' && (
						// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
						<UserStatus>{`(${formatMessage(messages.deactivated)})`}</UserStatus>
					)}
				</TextContainer>
			</Container>
		);
	},
);

export const SingleUserPickerWithProfileCardReadView = memo(
	({
		fragmentRef,
		emptyUserLabel,
		profileCardAction,
		position,
		shouldRenderHoverPopover,
		fieldId,
		humanSupervisor,
	}: SingleUserPickerWithProfileCardReadViewProps): JSX.Element => {
		const data = useFragment<SingleUserPickerWithProfileCardFragment>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment singleUserPicker_issueFieldSingleUserPickerReadviewFull_SingleUserPickerWithProfileCardReadView_fragmentRef on JiraSingleSelectUserPickerField {
					...singleUserPicker_issueFieldSingleUserPickerReadviewFull_SingleUserPickerReadView_fragmentRef
					user {
						accountId
						name
					}
				}
			`,
			fragmentRef,
		);

		const accountId: string | null = data?.user?.accountId || null;

		const actions = useMemo(
			(): Actions[] =>
				profileCardAction ? [VIEW_PROFILE_ACTION, profileCardAction] : [VIEW_PROFILE_ACTION],
			[profileCardAction],
		);

		if (accountId) {
			return (
				<MaybeUFOSegment>
					<ProfileCard
						prepopulatedData={{ fullName: data?.user?.name }}
						accountId={accountId}
						actions={actions}
						position={position}
					>
						<SingleUserPickerReadView
							fragmentRef={data}
							emptyUserLabel={emptyUserLabel}
							shouldRenderHoverPopover={shouldRenderHoverPopover}
							fieldId={fieldId}
							humanSupervisor={humanSupervisor}
						/>
					</ProfileCard>
				</MaybeUFOSegment>
			);
		}

		return (
			<MaybeUFOSegment>
				<SingleUserPickerReadView
					fragmentRef={data}
					emptyUserLabel={emptyUserLabel}
					shouldRenderHoverPopover={shouldRenderHoverPopover}
					fieldId={fieldId}
				/>
			</MaybeUFOSegment>
		);
	},
);

/**
 * Component to pass for "as" prop of Avatar. It reverses the 2px margin added in the Avatar
 * component when a transparent border is used.
 *
 * Done using div + css because styled components are not compatible with Avatar's types.
 */
const AvatarAlignmentFix = forwardRef<HTMLDivElement, React.AllHTMLAttributes<HTMLElement>>(
	(props, ref) => <div {...props} ref={ref} css={avatarAlignmentStyles} />,
);

const avatarAlignmentStyles = css({
	marginTop: token('space.negative.025'),
	marginRight: token('space.negative.025'),
	marginBottom: token('space.negative.025'),
	marginLeft: token('space.negative.025'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	maxWidth: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextContainer = styled.span({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UserStatus = styled.span({
	color: token('color.text.subtlest'),
	marginLeft: token('space.025'),
});
